import { useCallback, useEffect, useMemo, useState } from "react";
import { Avatar, Button, EmptyState, Icon, IconButton, Skeleton } from "@astrum/ui";
import { api } from "../../lib/api.js";
import type { Channel } from "../../lib/api.js";
import { useAuth } from "../../lib/auth.js";
import { hasChannelDraft, useDraftKeys } from "../../lib/drafts.js";
import { LabelsPanel, MessageFilterList } from "./ChannelFilters.js";
import { SavedList, ThreadsInbox } from "./ThreadsInbox.js";
import { FILTER_TABS, FLAG_FILTER_KIND } from "./types.js";
import type { FilterKind, SearchablePerson } from "./types.js";

interface ChannelListProps {
  channels: Channel[];
  loading: boolean;
  activeChannelId: string | undefined;
  onSelect: (channelId: string, messageId?: string) => void;
  /** Opens the new-channel dialog — a channel needs a name, topic, and project. */
  onNewChannel: () => void;
  /** Opens the searchable list of every public channel in the office. */
  onBrowseChannels: () => void;
  /** Opens the people picker for a group message. */
  onNewGroup: () => void;
  /** Marks every channel read in the active office. */
  onMarkAllRead: () => void;
  /** Opens (or starts) the direct conversation with someone. */
  onMessagePerson: (personId: string) => void;
  /** Set while a direct conversation is being opened. */
  openingPersonId: string | null;
  /** Which slice of the sidebar is showing — owned by the parent so the filter
   *  rail beside this list can share the same selection. */
  activeTab: FilterKind;
  /** Toggles whether a channel is pinned to the top of the viewer's own sidebar. */
  onTogglePin: (channel: Channel) => void;
  /** Opens a followed thread's panel, in its channel. */
  onOpenThread: (channelId: string, rootId: string) => void;
}

/**
 * The Lark-style filter rail: a narrow icon column of its own, to the left of
 * the channel list rather than a row of pills stuffed above its search box.
 * Desktop has the width to spare for a real fourth grid column; collapses to
 * a horizontal strip above the list on mobile (see chat.css).
 */
export function FilterRail({ active, onChange }: { active: FilterKind; onChange: (kind: FilterKind) => void }) {
  return (
    <nav className="chat-filter-rail" role="tablist" aria-label="Filter conversations">
      {FILTER_TABS.map((tab) => (
        <button
          key={tab.kind}
          type="button"
          role="tab"
          aria-selected={active === tab.kind}
          className={`chat-filter-rail__tab ${active === tab.kind ? "is-active" : ""}`}
          onClick={() => onChange(tab.kind)}
        >
          <Icon name={tab.icon} size={18} />
          <span>{tab.label}</span>
        </button>
      ))}
    </nav>
  );
}

/** Remembers which sections are collapsed, so the choice survives a reload. */
const COLLAPSED_SECTIONS_KEY = "astrum.chat.collapsed-sections";

function loadCollapsedSections(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSED_SECTIONS_KEY);
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch {
    // Private browsing, corrupted value, or storage full — start expanded.
    return new Set();
  }
}

export function ChannelList({
  channels,
  loading,
  activeChannelId,
  onSelect,
  onNewChannel,
  onBrowseChannels,
  onNewGroup,
  onMarkAllRead,
  onMessagePerson,
  openingPersonId,
  activeTab,
  onTogglePin,
  onOpenThread,
}: ChannelListProps) {
  const [filter, setFilter] = useState("");
  const draftKeys = useDraftKeys();
  const { viewer } = useAuth();
  const [people, setPeople] = useState<SearchablePerson[]>([]);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(loadCollapsedSections);

  const toggleSection = useCallback((key: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        localStorage.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify([...next]));
      } catch {
        // As above — the toggle still works for this session either way.
      }
      return next;
    });
  }, []);

  const query = filter.trim();

  /*
   * The directory is fetched once, on the first search rather than on mount:
   * most visits never search, and this keeps a list of everyone out of the
   * critical path for opening chat.
   */
  useEffect(() => {
    if (!query || people.length > 0) return;
    let cancelled = false;
    api
      .get<{ people: SearchablePerson[] }>("/people")
      .then((res) => !cancelled && setPeople(res.people))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [query, people.length]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    if (!q) return channels;
    return channels.filter((c) => c.name.toLowerCase().includes(q));
  }, [channels, query]);

  /*
   * People you have no conversation with yet, matching the search.
   *
   * Anyone already in the direct-message list is excluded — they are shown
   * above as a real conversation, and listing them twice would make the
   * second entry look like a way to start a *different* thread.
   */
  const strangers = useMemo(() => {
    const q = query.toLowerCase();
    if (!q) return [];
    const talkingTo = new Set(
      channels
        .filter((c) => c.kind === "direct")
        .map((c) => (c as Channel & { counterpartId?: string }).counterpartId)
        .filter(Boolean),
    );
    return people.filter(
      (p) =>
        p.id !== viewer?.id &&
        !talkingTo.has(p.id) &&
        [p.displayName, p.jobTitle, p.department].some((f) => f?.toLowerCase().includes(q)),
    );
  }, [people, channels, query, viewer?.id]);

  /*
   * The channel-subset filters — Unread and Groups narrow within a kind the
   * Chats view already shows; DMs is its own kind. Each is computed from the
   * same already-fetched channel list rather than a new endpoint, since the
   * data needed (kind, unreadCount) is already on every Channel.
   */
  const scoped = useMemo(() => {
    switch (activeTab) {
      case "unread":
        return filtered.filter((c) => !!c.unreadCount);
      case "dms":
        return filtered.filter((c) => c.kind === "direct" || c.kind === "group");
      case "groups":
        return filtered.filter((c) => c.kind === "team" || c.kind === "project");
      default:
        return filtered;
    }
  }, [filtered, activeTab]);

  const sections = useMemo(() => {
    // Pinned channels get their own section, most-recently-pinned first, and
    // are pulled out of whatever section they would otherwise land in below —
    // the point of pinning is not seeing the same channel listed twice.
    const pinned = scoped
      .filter((c) => !!c.pinnedAt)
      .sort((a, b) => (b.pinnedAt ?? "").localeCompare(a.pinnedAt ?? ""));
    const unpinned = scoped.filter((c) => !c.pinnedAt);
    const pinnedSection = pinned.length
      ? [{ key: "pinned", label: "Pinned", items: pinned, add: undefined }]
      : [];

    if (activeTab === "dms") {
      return [...pinnedSection, { key: "direct", label: "Direct messages", items: unpinned, add: undefined }];
    }
    if (activeTab === "groups") {
      return [
        ...pinnedSection,
        { key: "project", label: "Project channels", items: unpinned.filter((c) => c.kind === "project"), add: { label: "New channel" } },
        { key: "team", label: "Team", items: unpinned.filter((c) => c.kind === "team"), add: { label: "New channel" } },
      ];
    }
    if (activeTab === "unread") {
      return [...pinnedSection, { key: "unread", label: "Unread", items: unpinned, add: undefined }];
    }
    return [
      ...pinnedSection,
      { key: "project", label: "Project channels", items: unpinned.filter((c) => c.kind === "project"), add: { label: "New channel" } },
      { key: "team", label: "Team", items: unpinned.filter((c) => c.kind === "team"), add: { label: "New channel" } },
      { key: "direct", label: "Direct messages", items: unpinned.filter((c) => c.kind === "direct" || c.kind === "group"), add: undefined },
    ];
  }, [scoped, activeTab]);

  const nothingMatches = !!query && filtered.length === 0 && strangers.length === 0;
  const isChannelView = activeTab === "chats" || activeTab === "unread" || activeTab === "dms" || activeTab === "groups";
  const flagKind = FLAG_FILTER_KIND[activeTab];

  return (
    <aside className="chat-list" aria-label="Channels">
      <div className="chat-list__header">
        <h2>Chat</h2>
        <div className="chat-list__header-actions">
          {channels.some((c) => !!c.unreadCount) && (
            <Button size="sm" variant="ghost" onClick={onMarkAllRead}>
              Mark all read
            </Button>
          )}
          <IconButton icon={<Icon name="search" size={16} />} label="Browse channels" size="sm" onClick={onBrowseChannels} />
          <IconButton icon={<Icon name="people" size={16} />} label="New group message" size="sm" onClick={onNewGroup} />
          <IconButton icon={<Icon name="plus" size={16} />} label="New channel" size="sm" onClick={onNewChannel} />
        </div>
      </div>

      {isChannelView && (
        <label className="chat-list__search">
          <Icon name="search" size={14} />
          <input
            type="search"
            placeholder="Search conversations and people"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            aria-label="Search conversations and people"
          />
        </label>
      )}

      <div className="chat-list__scroll">
        {activeTab === "labels" ? (
          <LabelsPanel channels={channels} onOpenChannel={onSelect} />
        ) : activeTab === "mentions" ? (
          <MessageFilterList path="/me/mentioned-messages" emptyLabel="Nobody has mentioned you yet." onOpenChannel={onSelect} />
        ) : activeTab === "threads" ? (
          <ThreadsInbox onOpenThread={onOpenThread} />
        ) : activeTab === "saved" ? (
          <SavedList onOpenChannel={onSelect} />
        ) : flagKind ? (
          <MessageFilterList
            path={`/me/flagged-messages?kind=${flagKind}`}
            emptyLabel={`Nothing marked "${FILTER_TABS.find((t) => t.kind === activeTab)?.label}".`}
            onOpenChannel={onSelect}
          />
        ) : loading ? (
          <div className="chat-list__loading">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="chat-list__skeleton-row">
                <Skeleton circle width={28} height={28} />
                <Skeleton height={12} width="70%" />
              </div>
            ))}
          </div>
        ) : channels.length === 0 && !query ? (
          <EmptyState
            icon="chat"
            title="No conversations yet"
            description="Search for a colleague above, or create a channel."
            action={
              <Button size="sm" onClick={onNewChannel}>
                New channel
              </Button>
            }
          />
        ) : (
          <>
            {sections.map((section) => {
              // A section with a search applied and no matches is noise; an
              // empty one with no search still shows, because its "+" is the
              // way to create the first item in it.
              if (section.items.length === 0 && query) return null;
              const isCollapsed = collapsedSections.has(section.key);
              return (
                <div key={section.key}>
                  <div className="chat-list__label">
                    <button
                      type="button"
                      className="chat-list__label-toggle"
                      onClick={() => toggleSection(section.key)}
                      aria-expanded={!isCollapsed}
                    >
                      <Icon name={isCollapsed ? "chevron-right" : "chevron-down"} size={12} />
                      {section.label}
                    </button>
                    {section.add && (
                      <button
                        type="button"
                        className="chat-list__add"
                        title={section.add.label}
                        aria-label={section.add.label}
                        onClick={onNewChannel}
                      >
                        <Icon name="plus" size={12} />
                      </button>
                    )}
                  </div>
                  {isCollapsed ? null : section.items.length === 0 ? (
                    <p className="chat-list__no-match">Nothing here.</p>
                  ) : (
                    section.items.map((channel) => (
                      <div
                        key={channel.id}
                        className={`chat-list__row ${channel.pinnedAt ? "is-pinned" : ""}`}
                      >
                        <button
                          type="button"
                          className={`chat-list__item ${channel.id === activeChannelId ? "is-active" : ""}`}
                          onClick={() => onSelect(channel.id)}
                          aria-current={channel.id === activeChannelId ? "true" : undefined}
                        >
                          {channel.kind === "direct" || channel.kind === "group" ? (
                            <Avatar name={channel.name} size={22} />
                          ) : (
                            <span className="chat-list__hash" aria-hidden="true">
                              #
                            </span>
                          )}
                          <span className="chat-list__name" title={channel.name}>
                            {channel.name}
                          </span>
                          {hasChannelDraft(draftKeys, channel.id) && channel.id !== activeChannelId && (
                            <span className="chat-list__draft" title="Unsent draft" role="img" aria-label="Unsent draft">
                              <Icon name="edit" size={12} />
                            </span>
                          )}
                          {!!channel.unreadCount && (
                            <span className="chat-list__unread">{channel.unreadCount > 99 ? "99+" : channel.unreadCount}</span>
                          )}
                        </button>
                        <button
                          type="button"
                          className="chat-list__pin"
                          title={channel.pinnedAt ? "Unpin" : "Pin to top"}
                          aria-label={channel.pinnedAt ? `Unpin ${channel.name}` : `Pin ${channel.name} to top`}
                          onClick={() => onTogglePin(channel)}
                        >
                          <Icon name="pin" size={13} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              );
            })}

            {/* People you have not messaged, shown only while searching — the
                WhatsApp pattern. Without this the directory is unreachable from
                chat and a first conversation cannot be started. */}
            {activeTab === "chats" && strangers.length > 0 && (
              <div>
                <div className="chat-list__label">Other people</div>
                {strangers.map((person) => (
                  <button
                    key={person.id}
                    type="button"
                    className="chat-list__item chat-list__item--person"
                    onClick={() => onMessagePerson(person.id)}
                    disabled={openingPersonId !== null}
                  >
                    <Avatar name={person.displayName} src={person.avatarUrl ?? undefined} size={22} />
                    <span className="chat-list__name" title={person.displayName}>
                      {person.displayName}
                    </span>
                    <span className="chat-list__person-hint">
                      {openingPersonId === person.id ? <Icon name="spinner" size={13} /> : "Message"}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {nothingMatches && <p className="chat-list__no-match">Nothing matches “{query}”.</p>}
          </>
        )}
      </div>
    </aside>
  );
}
